import * as types from "./actionTypes";
import { API_ROOT } from "../../baseUrls";

function getCookie(name) {
  let regex = new RegExp(`\\b${name}=([^;]+)`);
  const match = document.cookie.match(regex);
  return match && match[1] ? match[1] : null;
}

function eraseCookie(name) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function buildLoginPayload(data) {
  return {
    uid: data.user_id,
    mid: data.mindfile_id,
    username: data.username,
    email: data.email,
    receive_email: data.receive_email,
    has_password: data.has_password,
    is_admin: data.is_admin,
    role: data.role,
    designee1: data.designee1,
    relationship1: data.relationship1,
    designee2: data.designee2,
    relationship2: data.relationship2,
    designee3: data.designee3,
    relationship3: data.relationship3,
    do_not_disclose: data.do_not_disclose,
    authenticity_token: data.authenticity_token,
    timestamp: data.timestamp,
    picture: `/mindfiles/${data.mindfile_id}/avatar/thumbnail?size=48&ts=${data.timestamp}`,
    facebook_access_token: getCookie("facebook_access_token"),
    instagram_access_token: getCookie("instagram_access_token"),
    twitter_access_token: getCookie("twitter_access_token"),
    twitter_access_secret: getCookie("twitter_access_secret"),
    twitter_access_props: { user_id: getCookie("twitter_access_user_id"), screen_name: getCookie("twitter_access_screen_name") },
  };
}

// Log in user with username and password
export function activateUser(token) {
  return function(dispatch, getState) {
    fetch(`${API_ROOT}/activate`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    })
      .then(resp => resp.json())
      .then(data => {
        if (data.status === "ok") {
          const resp = buildLoginPayload(data);
          if(data.redirect){
            resp["redirect"] = true;
          }
          dispatch({
            type: types.LOG_IN,
            payload: resp,
          });
        } else if (data.status === "error") {
          dispatch({
            type: types.SET_FAILED_LOGIN,
            payload: {
              failedLogin: true,
              failedLoginMessage: data.message,
            },
          });
        }
      })
      .catch(error => {
        console.error(error);
        dispatch({
          type: types.SET_FAILED_LOGIN,
          payload: {
            failedLogin: true,
            failedLoginMessage: "Server error.",
          },
        });
      });
  };
}

export function login(username, password) {
  return function(dispatch, getState) {
    fetch(`${API_ROOT}/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    })
      .then(resp => resp.json())
      .then(data => {
        if (data.status === "ok") {
          dispatch({
            type: types.LOG_IN,
            payload: buildLoginPayload(data),
          });
        } else if (data.status === "error") {
          dispatch({
            type: types.SET_FAILED_LOGIN,
            payload: {
              failedLogin: true,
              failedLoginMessage: data.message,
            },
          });
        }
      })
      .catch(error => {
        console.error(error);
        dispatch({
          type: types.SET_FAILED_LOGIN,
          payload: {
            failedLogin: true,
            failedLoginMessage: "Server error.",
          },
        });
      });
  };
}

// Check if user is logged in (using cookie value) and update state accordingly
export function loginWithCookie() {
    return function(dispatch, getState) {
        fetch(`${API_ROOT}/is-logged-in`, {
      method: "POST",
      credentials: "include",
    })
      .then(resp => resp.json())
      .then(data => {
        if (data.status === "ok") {
          if (data.logged_in === "true") {
            dispatch({
              type: types.LOG_IN,
              payload: buildLoginPayload(data),
            });
          } else {
            dispatch({
              type: types.EMPTY_SESSION_COOKIE,
              payload: {},
            });
          }
        }
      })
      .catch(error => {
        console.error(error);
        dispatch({
          type: types.EMPTY_SESSION_COOKIE,
          payload: {},
        });
      });
  };
}

// Log out user and clear session
export function logout() {
    return function(dispatch, getState) {
        const auth = getState().auth
    fetch(`${API_ROOT}/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {'X-CSRF-Token': auth.authenticity_token},
    })
      .then(resp => resp.json())
      .then(data => {
        dispatch({
          type: types.LOG_OUT,
          payload: {},
        });
      })
      .catch(error => {
        console.error(error);
        window.location = "/logout";
      });
  };
}

export function closeAccount() {
    return function(dispatch, getState) {
        const auth = getState().auth
    fetch(`${ API_ROOT }/users/${ auth.uid }/destroy`, {
      method: 'POST',
      credentials: 'include',
      headers: {'X-CSRF-Token': auth.authenticity_token},
    })
    .then(resp => {
      if (resp.ok) {
        dispatch({
          type: types.LOG_OUT,
          payload: {},
        });
        window.location.reload();
      }
    })
    .catch(error => console.error(error))
  };
}

// Clear data for failed login attempt
export function clearFailedLogin() {
  return dispatch => {
    dispatch({
      type: types.CLEAR_FAILED_LOGIN,
    });
  };
}

// Mark that login is in progress so we can display a 'in progress' spinner
export function waitForLogin() {
  return dispatch => {
    dispatch({
      type: types.WAIT_FOR_LOGIN,
      payload: {
        loginInProgress: true,
      },
    });
  };
}

export function resetPassword(username, recaptchaResponse) {
  const formData = new FormData();
  formData.append("user[username]", username);
  formData.append("g-recaptcha-response", recaptchaResponse)

  return function(dispatch) {
    fetch(`${API_ROOT}/reset`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then(resp => resp.json())
      .then(data => dispatch({ type: types.RESET_PASSWORD, payload: data }));
  };
}

export function clearResetPassword() {
    return function(dispatch) {
        dispatch({ type: types.RESET_PASSWORD, payload: null });
  };
}

export function markPasswordPresent() {
    return function(dispatch, getState) {
        if (!getState().auth.has_password) {
      dispatch({ type: types.SET_PASSWORD });
    }
  };
}

export function setFacebookAccessToken(token) {
    return function(dispatch) {
        if (token === null) {
      eraseCookie("facebook_access_token");
    }
    dispatch({ type: types.SET_FACEBOOK_ACCESS_TOKEN, payload: token });
  }
}

export function setInstagramAccessToken(token) {
    return function(dispatch) {
        if (token === null) {
      eraseCookie("instagram_access_token");
    }
    dispatch({ type: types.SET_INSTAGRAM_ACCESS_TOKEN, payload: token });
  }
}

export function setTwitterAccessToken(token, secret, user_id, screen_name) {
    return function(dispatch) {
    if (token === null) {
      eraseCookie("twitter_access_token");
    }
    if (secret === null) {
      eraseCookie("twitter_access_secret");
    }
    if (user_id === null) {
      eraseCookie("twitter_access_user_id");
    }
    if (screen_name === null) {
      eraseCookie("twitter_access_screen_name");
    }
    dispatch({ type: types.SET_TWITTER_ACCESS_TOKEN, payload: token });
    dispatch({ type: types.SET_TWITTER_ACCESS_SECRET, payload: secret });
    dispatch({ type: types.SET_TWITTER_ACCESS_PROPS, payload: {user_id: user_id, screen_name: screen_name} });
  }
}
